import React, { ReactElement } from 'react';
import { motion } from 'framer-motion';

import { FONT_WEIGHTS } from '~/constants/typography';

import { RendererProps } from '../../../models';
import { NOTE_KEYS } from '../../../constants/layout';
import { getPx } from '../../../helpers';
import { getNoteThemes } from './helpers';

function Note({ entity }: RendererProps): ReactElement {
  const pos = entity.getPos();
  const noteThemes = getNoteThemes(entity.windowDims);

  // Styling
  const noteB = entity.dims.b ?? 0;
  const noteHeight = getPx(entity.dims.h);
  const noteTheme = noteThemes[entity.theme];
  const noteBoxShadow = noteTheme.boxShadow;
  const noteStyle = {
    position: 'absolute' as const,
    width: getPx(entity.dims.w),
    height: noteHeight,
    left: getPx(pos.x - entity.dims.w / 2),
    top: getPx(pos.y - entity.dims.h / 2),
    textAlign: 'center' as const,
    lineHeight: noteHeight,
    borderRadius: getPx(3 * noteB),
    backgroundColor: noteTheme.backgroundColor,
    boxShadow: noteBoxShadow,
    overflow: 'hidden',
    zIndex: 4,
  };
  const backdropStyle = {
    position: 'absolute' as const,
    width: '100%',
    height: '100%',
    top: 0,
    left: 0,
    borderRadius: 'inherit',
    backgroundImage: noteTheme.backgroundImage,
    WebkitBackfaceVisibility: 'hidden' as const,
    zIndex: 0,
  };
  const labelStyle = {
    display: 'inline-block',
    position: 'relative' as const,
    lineHeight: 'normal',
    verticalAlign: 'middle',
    marginTop: `calc(-${noteHeight} / 8)`,
    fontSize: getPx((entity.dims.h * 3) / 7),
    fontWeight: FONT_WEIGHTS.medium,
    color: noteTheme.color,
    zIndex: 1,
  };

  // Animation
  const stateTheme = noteThemes[entity.state ?? NOTE_KEYS.miss];
  const stateBoxShadow = stateTheme.boxShadow;
  type PoofFrames = [null, string, string];
  const noteVariants = {
    reset: {
      backgroundColor: noteTheme.backgroundColor,
      boxShadow: noteBoxShadow,
      opacity: 1,
    },
    miss: {
      backgroundColor: stateTheme.backgroundColor,
      transition: { duration: 0.03 },
    },
    poof: {
      backgroundColor: [
        null,
        stateTheme.backgroundColor,
        stateTheme.backgroundColor,
      ] as PoofFrames,
      boxShadow: [null, stateBoxShadow, stateBoxShadow] as PoofFrames,
      opacity: [1, 1, 0],
      transition: {
        duration: 0.27,
        times: [0, 0.22, 1],
        ease: ['easeOut', 'linear'],
      },
    },
  };
  const backdropVariants = {
    reset: {
      opacity: 1,
    },
    miss: {
      opacity: 0,
      transition: { duration: 0.03 },
    },
    poof: {
      opacity: 0,
      transition: { duration: 0.0594, ease: 'easeOut' },
    },
  };
  const labelVariatns = {
    reset: {
      color: noteTheme.color,
    },
    miss: {
      color: stateTheme.color,
      transition: { duration: 0.03 },
    },
    poof: {
      color: stateTheme.color,
      transition: { duration: 0.0594, ease: 'easeOut' },
    },
  };
  const animate =
    entity.state && entity.state !== NOTE_KEYS.miss ? 'poof' : entity.state;

  return (
    <motion.div
      initial={false}
      style={noteStyle}
      animate={animate ?? 'reset'}
      variants={noteVariants}
    >
      <motion.div
        style={backdropStyle}
        animate={animate}
        variants={backdropVariants}
      />
      <motion.div style={labelStyle} animate={animate} variants={labelVariatns}>
        {entity.prompt}
      </motion.div>
    </motion.div>
  );
}

export default Note;
